import { Close, PhoneIphone } from "@mui/icons-material";
import { Box, Divider, Drawer, IconButton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../../components/UIToolkit/Badges/MetaChip";
import { SystemSwitch } from "../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemValidationTextField } from "../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { InfoIconWithTooltip } from "../../../../components/UIToolkit/molecules/InfoIconWithTooltip";
import { InlineHint } from "../../../../components/UIToolkit/molecules/InlineHint";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { FONT_SIZE, INK, RADIUS } from "../../../../theme/tokens";
import { DEFAULT_ADMIN_FORM_ROW } from "../utils";
import {
	ADMIN_NOTIFICATION_ITEMS,
	ADMIN_PERMISSION_CATALOG,
	type AdminPermissionMeta,
	type AdminPermissionSubgroup,
	isGranted,
	needsPhoneNumber,
	permissionsInSubgroup,
	storedValueForGrant,
} from "./adminPermissionCatalog";
import { ADMIN_ROLES, applyRoleChange, customizedPermissions, permissionSource, roleDefaultGrant } from "./adminRoles";
import type { AdminRole, AdminRoleDefaults, AdminUser } from "./types";

const PANEL_WIDTH_PX = 520;

const SUBGROUPS: readonly AdminPermissionSubgroup[] = ["scheduling", "peopleAndPay"] as const;

/** A section heading. */
const SectionTitle: FC<{ title: string; tooltip?: string }> = ({ title, tooltip }) => (
	<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
		<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 700, color: INK.PRIMARY }}>{title}</Typography>
		{tooltip && <InfoIconWithTooltip title={tooltip} iconSx={{ fontSize: 15, color: INK.MUTED }} />}
	</Box>
);

/**
 * One permission: its name, where its value came from, and the switch.
 *
 * The line under the label is the point of the panel — the grid says a permission is on, this says
 * why, and offers to put it back.
 */
const PermissionRow: FC<{
	row: AdminUser;
	meta: AdminPermissionMeta;
	defaults: AdminRoleDefaults;
	disabled: boolean;
	onToggle: (meta: AdminPermissionMeta) => void;
	onUseRoleDefault: (meta: AdminPermissionMeta) => void;
	onFocusPhone: () => void;
}> = ({ row, meta, defaults, disabled, onToggle, onUseRoleDefault, onFocusPhone }) => {
	const { t } = useTranslation();
	const granted = isGranted(row, meta);
	const source = permissionSource(row, meta, defaults);
	const isCustom = source.kind === "custom";
	const label = t(`admin.permission.${meta.i18nKey}.label`);
	const byText = needsPhoneNumber(meta.permission);
	const undeliverable = byText && granted && !row.phoneNumber?.trim();

	const roleNames = (roles: AdminRole[]) => roles.map((role) => t(`admin.roles.${role}`)).join(" + ");

	const sourceLine = (): string => {
		if (source.kind === "fromRoles") return t("admin.panel.source.fromRoles", { roles: roleNames(source.roles) });
		if (source.kind === "onWithoutRole") return t("admin.panel.source.onWithoutRole");
		if (source.kind === "notGranted") {
			return source.hasAnyRole ? t("admin.panel.source.notGrantedByRole") : t("admin.panel.source.noRoleAtAll");
		}
		// A switch on against the roles means no role turns it on.
		if (source.addedForPerson) return t("admin.panel.source.addedForPerson");
		return source.wouldGrant.length > 0
			? t("admin.panel.source.removedButRoleGrants", { roles: roleNames(source.wouldGrant) })
			: t("admin.panel.source.removedForPerson");
	};

	return (
		<Box
			data-testid={`admin-panel-row-${meta.permission}`}
			sx={(theme) => ({
				display: "flex",
				flexDirection: "column",
				gap: 0.75,
				px: 1.25,
				py: 1.25,
				borderRadius: `${RADIUS.CARD}px`,
				// A customization is deliberate, so tint it. Amber is for the blocker below.
				backgroundColor: isCustom ? alpha(theme.palette.info.main, 0.07) : "transparent",
				border: isCustom ? `1px solid ${alpha(theme.palette.info.main, 0.28)}` : "1px solid transparent",
			})}
		>
			<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
						<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, color: INK.PRIMARY }}>
							{label}
						</Typography>
						{byText && (
							<MetaChip
								label={t("admin.panel.byText")}
								icon={<PhoneIphone sx={{ fontSize: 12 }} />}
								tone={undeliverable ? "warning" : "neutral"}
								tooltip={t("admin.panel.byText.tooltip")}
							/>
						)}
						<InfoIconWithTooltip title={t(`admin.permission.${meta.i18nKey}.description`)} iconSx={{ fontSize: 14, color: INK.MUTED }} />
					</Box>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.25 }}>
						{isCustom && <MetaChip label={t("admin.panel.customTag")} tone="info" />}
						<Typography component="span" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
							{sourceLine()}
						</Typography>
						{/* No role, no role default to go back to. Same rule as the reset button. */}
						{isCustom && !disabled && row.adminRoles.length > 0 && (
							<SystemButton
								buttonVariant="borderless"
								size="small"
								onClick={() => onUseRoleDefault(meta)}
								data-testid={`admin-panel-revert-${meta.permission}`}
								sx={{ minWidth: 0, p: 0, fontSize: FONT_SIZE.CAPTION, textDecoration: "underline" }}
							>
								{t("admin.panel.useRoleDefault")}
							</SystemButton>
						)}
					</Box>
				</Box>
				<SystemSwitch
					checked={granted}
					onChange={() => onToggle(meta)}
					disabled={disabled}
					ariaLabel={label}
					dataTestId={`admin-panel-switch-${meta.permission}`}
				/>
			</Box>
			{undeliverable && (
				<InlineHint
					tone="warning"
					action={
						<SystemButton buttonVariant="borderless" size="small" onClick={onFocusPhone} data-testid="admin-panel-add-number">
							{t("admin.panel.addNumber")}
						</SystemButton>
					}
				>
					{t("admin.panel.undeliverable")}
				</InlineHint>
			)}
		</Box>
	);
};

/**
 * The per-admin access panel: identity, roles, and every permission with the reason for its value.
 *
 * The grid answers "who has what"; this answers "why". Edits land on the row in the parent's state.
 */
export const AdminAccessPanel: FC<{
	row: AdminUser | null;
	defaults: AdminRoleDefaults | undefined;
	open: boolean;
	onClose: () => void;
	onChange: (next: AdminUser) => void;
	onSave: () => void | Promise<void>;
	isSaving: boolean;
	disabled: boolean;
	/** This row has unsaved edits. */
	isDirty: boolean;
	/** A draft row: the panel is the create flow, so it invites rather than saves. */
	isNew: boolean;
	/** Drops the draft row. Only used while `isNew`. */
	onDiscardNew?: () => void;
	/** Discards unsaved edits and closes. Falls back to a plain close. */
	onCancelEdits?: () => void;
	/** Sends the reset link. Omitted when there is no email and no mobile. */
	onResetPassword?: () => void;
	/** Support-only: set a password directly, for the offline case. */
	onSetPassword?: () => void;
	/** A reset is in flight, so the button locks. */
	isResettingPassword?: boolean;
}> = ({
	row,
	defaults,
	open,
	onClose,
	onChange,
	onSave,
	isSaving,
	disabled,
	isDirty,
	isNew,
	onDiscardNew,
	onCancelEdits,
	onResetPassword,
	onSetPassword,
	isResettingPassword = false,
}) => {
	const { t } = useTranslation();

	const customized = useMemo(() => (row && defaults ? customizedPermissions(row, defaults) : []), [row, defaults]);

	// A first name is all the save has ever required (`validateAdminUsers`).
	const hasName = !!row?.firstName?.trim();
	// A draft arrives prefilled, so that is satisfied before anyone types. Refuse those exact values,
	// or Add user + Send invite creates an admin called "firstName". Compared against the constant.
	const isUntouchedPlaceholder =
		isNew && (row?.firstName?.trim() === DEFAULT_ADMIN_FORM_ROW.firstName || row?.username?.trim() === DEFAULT_ADMIN_FORM_ROW.username);
	const canSubmit = isNew ? hasName && !isUntouchedPlaceholder : isDirty && hasName;

	const toggleRole = useCallback(
		(role: AdminRole) => {
			if (!row || !defaults) return;
			const nextRoles = row.adminRoles.includes(role) ? row.adminRoles.filter((r) => r !== role) : [...row.adminRoles, role];
			onChange(applyRoleChange({ row, previousRoles: row.adminRoles, nextRoles, defaults }));
		},
		[row, defaults, onChange],
	);

	const toggleGrant = useCallback(
		(meta: AdminPermissionMeta) => {
			if (!row) return;
			onChange({ ...row, [meta.field]: storedValueForGrant(meta, !isGranted(row, meta)) });
		},
		[row, onChange],
	);

	const useRoleDefault = useCallback(
		(meta: AdminPermissionMeta) => {
			if (!row || !defaults) return;
			onChange({ ...row, [meta.field]: storedValueForGrant(meta, roleDefaultGrant(row.adminRoles, meta, defaults)) });
		},
		[row, defaults, onChange],
	);

	/** Puts every permission back to what the roles say. */
	const resetAllToRoleDefaults = useCallback(() => {
		if (!row || !defaults) return;
		let next = { ...row };
		for (const meta of ADMIN_PERMISSION_CATALOG) {
			next = { ...next, [meta.field]: storedValueForGrant(meta, roleDefaultGrant(row.adminRoles, meta, defaults)) };
		}
		onChange(next);
	}, [row, defaults, onChange]);

	const focusPhone = useCallback(() => {
		document.querySelector<HTMLInputElement>('[data-testid="admin-panel-phone"] input')?.focus();
	}, []);

	const renderRow = (meta: AdminPermissionMeta) =>
		row && defaults ? (
			<PermissionRow
				key={meta.permission}
				row={row}
				meta={meta}
				defaults={defaults}
				disabled={disabled}
				onToggle={toggleGrant}
				onUseRoleDefault={useRoleDefault}
				onFocusPhone={focusPhone}
			/>
		) : null;

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			slotProps={{ paper: { sx: { width: { xs: "100%", sm: PANEL_WIDTH_PX }, maxWidth: "100%" } } }}
			data-testid="admin-access-panel"
		>
			{row && (
				<Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, px: 2.5, pt: 2.5, pb: 1.5 }}>
						<Box sx={{ flex: 1, minWidth: 0 }}>
							<Typography sx={{ fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, color: INK.PRIMARY, lineHeight: 1.25 }}>
								{isNew ? t("admin.panel.addUser") : row.firstName?.trim() || t("admin.panel.unnamed")}
							</Typography>
							<Typography sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
								{isNew ? t("admin.panel.addUser.subtitle") : row.username || t("admin.contact.emailPlaceholder")}
							</Typography>
						</Box>
						<IconButton onClick={onClose} aria-label={t("admin.panel.close")} data-testid="admin-panel-close" size="small">
							<Close sx={{ fontSize: 20 }} />
						</IconButton>
					</Box>
					<Divider />

					<Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
						<Box sx={{ mb: 3 }}>
							<SectionTitle title={t("admin.panel.identity")} />
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
								<SystemValidationTextField
									label={t("admin.column.name")}
									value={row.firstName || ""}
									onChange={(e) => onChange({ ...row, firstName: e.target.value })}
									disabled={disabled}
									required
									fullWidth
									dataTestId="admin-panel-name"
								/>
								<SystemValidationTextField
									label={t("admin.panel.emailLabel")}
									value={row.username || ""}
									onChange={(e) => onChange({ ...row, username: e.target.value })}
									disabled={disabled}
									fullWidth
									dataTestId="admin-panel-email"
								/>
								<SystemValidationTextField
									label={t("admin.panel.mobileLabel")}
									value={row.phoneNumber || ""}
									onChange={(e) => onChange({ ...row, phoneNumber: e.target.value })}
									disabled={disabled}
									fullWidth
									dataTestId="admin-panel-phone"
								/>
							</Box>
							{!isNew && (onResetPassword || onSetPassword) && (
								<Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
									{onResetPassword && (
										<SystemButton
											buttonVariant="secondary"
											size="small"
											onClick={onResetPassword}
											loading={isResettingPassword}
											disabled={isResettingPassword}
											data-testid="admin-panel-reset-password"
										>
											{t("admin.menu.resetPassword")}
										</SystemButton>
									)}
									{onSetPassword && (
										<SystemButton buttonVariant="secondary" size="small" onClick={onSetPassword} data-testid="admin-panel-set-password">
											{t("admin.menu.setPassword")}
										</SystemButton>
									)}
								</Box>
							)}
						</Box>

						<Box sx={{ mb: 3 }}>
							<SectionTitle title={t("admin.panel.roles")} tooltip={t("admin.panel.roles.tooltip")} />
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
								{ADMIN_ROLES.map((role) => {
									const selected = row.adminRoles.includes(role);
									return (
										<SystemButton
											key={role}
											buttonVariant={selected ? "primary" : "secondary"}
											size="small"
											onClick={() => toggleRole(role)}
											disabled={disabled || !defaults}
											aria-pressed={selected}
											data-testid={`admin-panel-role-${role}`}
										>
											{t(`admin.roles.${role}`)}
										</SystemButton>
									);
								})}
							</Box>
							{/* Without the matrix the role buttons are disabled; say why rather than leaving four dead buttons. */}
							{!defaults && (
								<InlineHint tone="warning" sx={{ mt: 1.5 }}>
									{t("admin.roles.defaults.unavailable")}
								</InlineHint>
							)}
							{defaults && row.adminRoles.length === 0 && (
								<InlineHint tone={isNew ? "info" : "warning"} sx={{ mt: 1.5 }}>
									{t("admin.panel.noRoleHint")}
								</InlineHint>
							)}
						</Box>

						<Box sx={{ mb: 3 }}>
							<SectionTitle title={t("admin.column.permissions")} tooltip={t("admin.panel.permissions.tooltip")} />
							{SUBGROUPS.map((subgroup) => (
								<Box key={subgroup} sx={{ mb: 1.5 }}>
									<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, textTransform: "uppercase", letterSpacing: "0.08em", color: INK.MUTED, mb: 0.5, px: 1.25 }}>
										{t(`admin.panel.group.${subgroup}`)}
									</Typography>
									{permissionsInSubgroup(subgroup).map(renderRow)}
								</Box>
							))}
						</Box>

						<Box>
							<SectionTitle title={t("admin.column.notifications")} tooltip={t("admin.panel.notifications.tooltip")} />
							{ADMIN_NOTIFICATION_ITEMS.map(renderRow)}
						</Box>
					</Box>

					<Divider />
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.5, py: 2 }}>
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={resetAllToRoleDefaults}
							// With no role, a reset would turn off every switch except the three that default on.
							disabled={disabled || customized.length === 0 || row.adminRoles.length === 0}
							tooltip={row.adminRoles.length === 0 ? t("admin.panel.resetNeedsRole") : undefined}
							data-testid="admin-panel-reset"
						>
							{customized.length > 0 ? t("admin.panel.resetCount", { count: customized.length }) : t("admin.panel.noCustomizations")}
						</SystemButton>
						<Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
							<SystemButton
								buttonVariant="secondary"
								size="small"
								onClick={isNew ? (onDiscardNew ?? onClose) : (onCancelEdits ?? onClose)}
								data-testid="admin-panel-cancel"
							>
								{t("admin.panel.cancel")}
							</SystemButton>
							<SystemButton
								buttonVariant="primary"
								size="small"
								onClick={onSave}
								loading={isSaving}
								disabled={disabled || !canSubmit}
								data-testid="admin-panel-save"
							>
								{isNew ? t("admin.panel.sendInvite") : t("admin.panel.save")}
							</SystemButton>
						</Box>
					</Box>
				</Box>
			)}
		</Drawer>
	);
};
